import cn from 'classnames'
import BaseModal, {
  BaseModalProps,
} from 'ext-ui/components/BaseModal/BaseModal'
import Markdown from 'markdown-to-jsx'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import LockedNew from 'src/assets/images/LockedNew.svg'
import PaymentGateBanner from 'src/assets/images/PaymentGateBanner.svg'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import { useTranslate } from 'src/localization/Trns'
import { upgradeSubscriptionUrl } from 'src/navigation/urls'
import {
  usePostEnterpriseRequest,
  usePutManageSubscription,
} from 'src/queries/organizations/saas.queries'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'

import style from './SaasActionRequiredModal.style'

export enum SaasActionRequiredType {
  PAYMENT_REQUIRED = 'PAYMENT_REQUIRED',
  DOWNGRADE_ACTION_REQUIRED = 'DOWNGRADE_ACTION_REQUIRED',
}

// True when the global axios interceptor (App.tsx) will open one of the two
// paywall modals for this response. Reducers should skip rendering their own
// form error in this case to avoid double-rendering the backend message.
// Gated on status === 402 AND a known exceptionType so a future BE 402 the
// interceptor doesn't render still surfaces the form error.
export const isHandledPaywall402 = (payload: {
  status: number
  exceptionType?: SaasActionRequiredType
}): boolean =>
  payload.status === 402 &&
  (payload.exceptionType === SaasActionRequiredType.PAYMENT_REQUIRED ||
    payload.exceptionType === SaasActionRequiredType.DOWNGRADE_ACTION_REQUIRED)

enum UpgradeOption {
  PRO = 'PRO',
  ENTERPRISE = 'ENTERPRISE',
  MANAGE_SUBSCRIPTION = 'MANAGE_SUBSCRIPTION',
}

type SaasActionRequiredProps = BaseModalProps & {
  exceptionType?: SaasActionRequiredType
  error?: string
  upgradeOptions?: UpgradeOption[]
  header?: string
  footer?: string
  buttonText?: string
  analytics?: {
    event: string
    properties: Record<string, string>
  }
}

const SaasActionRequiredModal: React.FC<SaasActionRequiredProps> = ({
  isOpen,
  onClose,
  className,
  ...saasActionRequiredPopupProps
}): React.ReactElement => {
  const translate = useTranslate()

  const history = useHistory()

  const { mutate: postEnterpriseRequest } = usePostEnterpriseRequest()
  const { mutate: putManageSubscription } = usePutManageSubscription()

  const orgId = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )?.id
  const { analytics } = useAnalyticsContext()

  useEffect(() => {
    if (saasActionRequiredPopupProps?.analytics) {
      analytics.track(
        saasActionRequiredPopupProps.analytics.event,
        saasActionRequiredPopupProps.analytics.properties,
      )
    }
  }, [analytics, saasActionRequiredPopupProps?.analytics])

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      variant="interactive"
      className={cn('SaasActionRequiredModal', className)}
      css={style}
      aria-label={translate('SaaS Action Required Modal')}
    >
      <img src={PaymentGateBanner} alt={''} />
      <div className="SaasActionRequiredModal-Text">
        <header>{saasActionRequiredPopupProps?.header}</header>
        <p data-testid="saas-action-required-message">
          {/* Backend (extend-api PR #9587) emits **bold** markdown inside
              `error` so per-month/total-active card counts render bolded.
              Raw HTML parsing stays off — same pattern as
              DowngradedToStarterModal. */}
          <Markdown
            options={{ disableParsingRawHTML: true, forceInline: true }}
          >
            {saasActionRequiredPopupProps?.error ?? ''}
          </Markdown>
        </p>
      </div>
      <div className="SaasActionRequiredModal-CTA">
        {saasActionRequiredPopupProps?.buttonText && (
          <Button
            testId={'saasUpgradeSubmit'}
            design={ButtonDesigns.designCta}
            type={ButtonTypes.button}
            text={saasActionRequiredPopupProps?.buttonText}
            className={'SaasActionRequiredModal-CTA-Button'}
            onClick={(): void => {
              if (
                saasActionRequiredPopupProps.upgradeOptions?.includes(
                  UpgradeOption.ENTERPRISE,
                )
              ) {
                postEnterpriseRequest({
                  orgId: orgId || '',
                  body: {
                    intention:
                      saasActionRequiredPopupProps?.header ||
                      'Unknown Enterprise Pay Gate',
                  },
                })
              } else if (
                saasActionRequiredPopupProps.upgradeOptions?.includes(
                  UpgradeOption.MANAGE_SUBSCRIPTION,
                )
              ) {
                putManageSubscription({
                  orgId: orgId || '',
                  redirect: true,
                  body: {},
                })
              } else {
                history.push(upgradeSubscriptionUrl())
              }
              onClose()
            }}
          />
        )}
        {saasActionRequiredPopupProps.exceptionType ===
          SaasActionRequiredType.PAYMENT_REQUIRED && (
          <HoverableLink
            href={
              'https://support.paywithextend.com/hc/en-us/articles/32069180604951-How-do-I-upgrade-my-tier'
            }
            ariaLabelSource={saasActionRequiredPopupProps?.header || ''}
          >
            {translate('Learn more')}
          </HoverableLink>
        )}
      </div>
      {saasActionRequiredPopupProps?.footer && (
        <div className={'SaasActionRequiredModal-Footer'}>
          <img src={LockedNew} alt={''} />
          <span>{saasActionRequiredPopupProps?.footer}</span>
        </div>
      )}
    </BaseModal>
  )
}
export default SaasActionRequiredModal
